import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { Camera, ArrowLeft, Play, Pause } from 'iconoir-react';
import { getLojaInfo } from '../services/api';

const QRScanner = ({ brand }) => {
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);

  useEffect(() => {
    // Verificar fechamento temporário
    if (brand) {
      getLojaInfo(brand).then(res => {
        if (res?.loja?.fechamento_temporario?.ativo) {
          navigate('/', { replace: true });
        }
      }).catch(() => {});
    }

    QrScanner.hasCamera().then(hasCamera => {
      setHasCamera(hasCamera);
      if (!hasCamera) {
        setError('Nenhuma câmera encontrada no dispositivo.');
      }
    });

    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.destroy();
      }
    };
  }, []);

  const startScanning = async () => {
    if (!hasCamera || !videoRef.current) return;

    try {
      setError('');

      if (qrScannerRef.current) {
        qrScannerRef.current.destroy();
      }

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => handleScan(result.data),
        {
          onDecodeError: () => {},
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment'
        }
      );

      await qrScannerRef.current.start();
      setScanning(true);
    } catch (err) {
      setError('Erro ao acessar a câmera. Verifique as permissões.');
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      setScanning(false);
    }
  };

  const handleScan = (data) => {
    if (data) {
      stopScanning();

      try {
        const url = new URL(data);
        const pathname = url.pathname;
        const pathParts = pathname.split('/').filter(part => part);

        if (pathParts.length >= 2 && pathParts[0] === 'dinein') {
          navigate(`/dinein/${pathParts[1]}`);
          return;
        }

        if (pathParts.length >= 2 && pathParts[0] === 'mesa') {
          navigate(`/dinein/${pathParts[1]}`);
          return;
        }

        if (pathname.includes('/delivery')) {
          navigate('/delivery');
        } else if (pathname.includes('/balcao')) {
          navigate('/balcao');
        } else {
          navigate('/cardapio');
        }
      } catch (err) {
        if (data.includes('/dinein/')) {
          const token = data.split('/dinein/')[1].split('/')[0].split('?')[0];
          if (token) { navigate(`/dinein/${token}`); return; }
        }

        if (data.includes('/mesa/')) {
          const token = data.split('/mesa/')[1].split('/')[0].split('?')[0];
          if (token) { navigate(`/dinein/${token}`); return; }
        }

        if (data.length > 5 && data.length < 50 && !data.includes(' ')) {
          navigate(`/dinein/${data}`);
          return;
        }

        setError('QR Code inválido. Tente novamente.');
        setTimeout(() => {
          setError('');
          if (hasCamera) startScanning();
        }, 2000);
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh', padding: '24px 16px' }}>
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <ArrowLeft width={24} height={24} />
          </button>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Ler QR Code</h2>
        </div>

        {/* Card principal */}
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e8eaed', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
          {/* Video */}
          <div style={{ position: 'relative', background: '#000' }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '320px',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            {!scanning && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.6)',
                gap: '12px'
              }}>
                <Camera width={48} height={48} color="#fff" />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', fontWeight: 500 }}>
                  Aponte para o QR Code da mesa
                </span>
              </div>
            )}
          </div>

          {/* Controles */}
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            {hasCamera && (
              <button
                onClick={scanning ? stopScanning : startScanning}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  background: scanning ? '#dc2626' : '#0039ff',
                  color: '#fff'
                }}
              >
                {scanning ? (
                  <><Pause width={18} height={18} /> Parar Scanner</>
                ) : (
                  <><Play width={18} height={18} /> Iniciar Scanner</>
                )}
              </button>
            )}

            <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
              {scanning ? 'Posicione o QR Code dentro da área de leitura' : 'Clique para iniciar a câmera'}
            </p>

            <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '10px', textAlign: 'left' }}>
              <p style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Instruções:</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
                Aponte a câmera para o QR Code que está na mesa. Ele contém o número da sua mesa e vai direcionar você automaticamente para fazer seu pedido.
              </p>
            </div>

            <div style={{ marginTop: '16px' }}>
              <a href="https://grub.digital" target="_blank" rel="noopener noreferrer">
                <img src="https://grubicons.s3.us-east-1.amazonaws.com/powered-by-grub.png" alt="Powered by Grub" style={{ maxWidth: '140px', width: '100%' }} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
